import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import ListLayout from '../../Generic/ListLayout';
import DataTable from '../../Generic/DataTable';
import confirmDeleteDialog from '../../Generic/confirmDeleteDialog';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useDeleteItem from '../../../hooks/useDeleteItem';
import useFilterData from '../../../hooks/useFilterData';
import useFetchData from '../../../hooks/useFetchData';
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../hooks/useSetMenu";
import { createGeneralColumn, createActiveColumn, createActionColumn } from '../../Generic/dataGridColumns';
import AuthContext from "../../../context/AuthProvider";
import FormSelect from '../../Generic/FormSelect';
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import './ToolFeature.css';

// MUI Icons
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchOffIcon from '@mui/icons-material/SearchOff';

const List = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);
  const { mainStore, toolFeatureStore } = useStore();
  const { t } = useTranslation();
  const localizedTextsMap = useLocalizedTextsMap();
  useSetMenu(mainStore.ToolFeatureMenuType, mainStore.ToolFeatureMenuNo);

  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  // First group: Panel, Category, Subcategory
  const [panels, setPanels] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState(null);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // Second group: Tool Passport, Criteria, Criteria Value
  const [toolPassports, setToolPassports] = useState([]);
  const [selectedToolPassport, setSelectedToolPassport] = useState(null);

  const [criteriaOptions, setCriteriaOptions] = useState([]);
  const [selectedCriteria, setSelectedCriteria] = useState(null);

  const [criteriaValueOptions, setCriteriaValueOptions] = useState([]);
  const [selectedCriteriaValue, setSelectedCriteriaValue] = useState(null);

  const { register, setValue, formState: { errors } } = useForm({});

  // Delete operation - Define this BEFORE it's used in columns 
  const deleteToolFeature = useDeleteItem(axiosPrivate, '/ToolFeatures', toolFeatureStore.deleteToolFeature);
  const remove = useCallback((id) => {
    confirmDeleteDialog(t, deleteToolFeature, id);
  }, [deleteToolFeature, t]);

  // Load initial options for Panels on mount.
  useEffect(() => {
    const loadInitialOptions = async () => {
      try {
        const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
        setPanels(panelsResponse.data);
      } catch (error) {
        enqueueSnackbar(t("loadDataError"), { variant: "error" });
      }
    };
    loadInitialOptions();
  }, [axiosPrivate, enqueueSnackbar, t]);

  // Panel change: load categories for the selected panel and clear dependent filters.
  const handlePanelChange = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedPanel(null);
      setCategories([]);
      setSelectedCategory(null);
      setSubcategories([]);
      setSelectedSubcategory(null);
      // Also clear tool passports and subsequent filters.
      setToolPassports([]);
      setSelectedToolPassport(null);
      setCriteriaOptions([]);
      setSelectedCriteria(null);
      setCriteriaValueOptions([]);
      setSelectedCriteriaValue(null);
      return;
    }
    setSelectedPanel(selectedOption);
    try {
      const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
      setCategories(typeof response.data === "string" && response.data === "No records" ? [] : response.data);
      setSelectedCategory(null);
      setSubcategories([]);
      setSelectedSubcategory(null);
      setToolPassports([]);
      setSelectedToolPassport(null);
      setCriteriaOptions([]);
      setSelectedCriteria(null);
      setCriteriaValueOptions([]);
      setSelectedCriteriaValue(null);
    } catch (error) {
      enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
    }
  };

  // Category change: load subcategories for the selected category and clear dependent filters.
  const handleCategoryChange = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedCategory(null);
      setSubcategories([]);
      setSelectedSubcategory(null);
      setToolPassports([]);
      setSelectedToolPassport(null);
      setCriteriaOptions([]);
      setSelectedCriteria(null);
      setCriteriaValueOptions([]);
      setSelectedCriteriaValue(null);
      return;
    }
    setSelectedCategory(selectedOption);
    try {
      const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
      setSubcategories(typeof response.data === "string" && response.data === "No records" ? [] : response.data);
      setSelectedSubcategory(null);
      setToolPassports([]);
      setSelectedToolPassport(null);
      setCriteriaOptions([]);
      setSelectedCriteria(null);
      setCriteriaValueOptions([]);
      setSelectedCriteriaValue(null);
    } catch (error) {
      enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
    }
  };

  // Subcategory change: load Tool Passport options and load Criteria options based on the subcategory.
  const handleSubcategoryChange = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedSubcategory(null);
      setToolPassports([]);
      setSelectedToolPassport(null);
      setCriteriaOptions([]);
      setSelectedCriteria(null);
      setCriteriaValueOptions([]);
      setSelectedCriteriaValue(null);
      return;
    }
    setSelectedSubcategory(selectedOption);
    try {
      // Load Tool Passport options based on subcategory.
      const toolPassportsResponse = await axiosPrivate.get(`/ToolPassports/SelectOptionsBySubcategory/${selectedOption.value}`);
      setToolPassports(typeof toolPassportsResponse.data === "string" && toolPassportsResponse.data === "No records" ? [] : toolPassportsResponse.data);
      setSelectedToolPassport(null);
    } catch (error) {
      enqueueSnackbar(t("errorLoadingToolPassports"), { variant: "error" });
    }
    try {
      // Load Criteria options based on subcategory.
      const criteriaResponse = await axiosPrivate.get(`/Criterias/SelectOptionsBySubcategory/${selectedOption.value}`);
      setCriteriaOptions(typeof criteriaResponse.data === "string" && criteriaResponse.data === "No records" ? [] : criteriaResponse.data);
      setSelectedCriteria(null);
      // Clear criteria value options until a criteria is chosen.
      setCriteriaValueOptions([]);
      setSelectedCriteriaValue(null);
    } catch (error) {
      enqueueSnackbar(t("errorLoadingCriteria"), { variant: "error" });
    }
  };

  // Tool Passport change
  const handleToolPassportChange = (selectedOption) => {
    if (!selectedOption) {
      setSelectedToolPassport(null);
      return;
    }
    setSelectedToolPassport(selectedOption);
  };

  // Criteria change: load Criteria Value options based on the selected criteria.
  const handleCriteriaChange = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedCriteria(null);
      setCriteriaValueOptions([]);
      setSelectedCriteriaValue(null);
      return;
    }
    setSelectedCriteria(selectedOption);
    try {
      const response = await axiosPrivate.get(`/CriteriaValues/SelectOptionsByCriteria/${selectedOption.value}`);
      setCriteriaValueOptions(typeof response.data === "string" && response.data === "No records" ? [] : response.data);
      setSelectedCriteriaValue(null);
    } catch (error) {
      enqueueSnackbar(t("errorLoadingCriteriaValues"), { variant: "error" });
    }
  };

  // Criteria Value change
  const handleCriteriaValueChange = (selectedOption) => {
    if (!selectedOption) {
      setSelectedCriteriaValue(null);
      return;
    }
    setSelectedCriteriaValue(selectedOption);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // This component displays multiple criteria values in a readable format
  const CriteriaValuesList = ({ criteriaValues }) => {
    if (!criteriaValues || criteriaValues.length === 0) {
      return <span>-</span>;
    }
    
    // Group by criteria name
    const groupedByCriteria = {};
    criteriaValues.forEach(cv => {
      if (!groupedByCriteria[cv.criteriaName]) {
        groupedByCriteria[cv.criteriaName] = [];
      }
      groupedByCriteria[cv.criteriaName].push(cv.value);
    });

    // Create a formatted list
    return (
      <div>
        {Object.entries(groupedByCriteria).map(([criteriaName, values]) => (
          <div key={criteriaName} style={{ marginBottom: "0.25rem" }}>
            <strong>{criteriaName}:</strong>{" "}
            {values.join(", ")}
          </div>
        ))}
      </div>
    );
  };

  // Create columns using useMemo - Now the 'remove' function is already defined above
  const columns = useMemo(() => [
    createGeneralColumn("toolPassportName", "toolPassport", t,230),
    // createGeneralColumn("criteriaValueName", "criteriaValue", t),
    // createGeneralColumn("criteriaName", "criteria", t),
    createGeneralColumn("subcategoryName", "subcategory", t ,450),
    createGeneralColumn("categoryName", "category", t, 250),
    createGeneralColumn("panelName", "panel", t, 115),
    // createActiveColumn(t),
    // {
    //   Header: t("criteriaValues"),
    //   accessor: "criteriaValues",
    //   Cell: ({ value }) => <CriteriaValuesList criteriaValues={value} />
    // },
    createActionColumn(t, "/ToolFeature/Edit", remove)
  ], [t, remove, localizedTextsMap, auth]);

  const filteredData = useFilterData(toJS(toolFeatureStore.toolFeature), searchQuery);

  // Fetch filtered ToolFeatures whenever any filter changes.
  useEffect(() => {
    const fetchToolFeatures = async () => {
      try {
        const queryParams = [];
        if (selectedPanel) queryParams.push(`panelId=${selectedPanel.value}`);
        if (selectedCategory) queryParams.push(`categoryId=${selectedCategory.value}`);
        if (selectedSubcategory) queryParams.push(`subcategoryId=${selectedSubcategory.value}`);
        if (selectedToolPassport) queryParams.push(`toolPassportId=${selectedToolPassport.value}`);
        if (selectedCriteria) queryParams.push(`criteriaId=${selectedCriteria.value}`);
        if (selectedCriteriaValue) queryParams.push(`criteriaValueId=${selectedCriteriaValue.value}`);
        if (searchQuery) queryParams.push(`search=${encodeURIComponent(searchQuery)}`);
        const queryString = queryParams.length ? `?${queryParams.join('&')}` : "";
        const response = await axiosPrivate.get(`/ToolFeatures/Filtered${queryString}`);
        if (Array.isArray(response.data)) {
          toolFeatureStore.setData(response.data);
        } else {
          toolFeatureStore.setData([]);
        }
      } catch (error) {
        enqueueSnackbar(t("errorLoadingData"), { variant: "error" });
      }
    };
    fetchToolFeatures();
  }, [
    selectedPanel,
    selectedCategory,
    selectedSubcategory,
    selectedToolPassport,
    selectedCriteria,
    selectedCriteriaValue,
    searchQuery,
    axiosPrivate,
    toolFeatureStore,
    enqueueSnackbar,
    t,
  ]);

  return (
    <ListLayout
      title={t("toolFeatureListTitle")}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      searchPlaceholder={t("searchPlaceholder")}
      addButtonLink="/ToolFeature/Add"
      addButtonTitle={t("add")}
    >
      {/* Modern Filter Card */}
      <div className="tool-feature-filters">
        <div className="tool-feature-filters-header">
          <FilterListIcon />
          <span>{t("filters")}</span>
        </div>
        <div className="tool-feature-filters-body">
          <div className="row">
            <FormSelect
              label="panel"
              name="panelId"
              register={register}
              errors={errors}
              options={panels}
              onChange={handlePanelChange}
              isClearable={true}
            />
            <FormSelect
              label="category"
              name="categoryId"
              value={selectedCategory}
              register={register}
              errors={errors}
              options={categories}
              onChange={handleCategoryChange}
              isClearable={true}
            />
            <FormSelect
              label="subcategory"
              name="subcategoryId"
              value={selectedSubcategory}
              register={register}
              errors={errors}
              options={subcategories}
              onChange={handleSubcategoryChange}
              isClearable={true}
            />
            <FormSelect
              label="toolPassport"
              name="toolPassportId"
              value={selectedToolPassport}
              register={register}
              errors={errors}
              options={toolPassports}
              onChange={handleToolPassportChange}
              isClearable={true}
            />
            <FormSelect
              label="criteria"
              name="criteriaId"
              value={selectedCriteria}
              register={register}
              errors={errors}
              options={criteriaOptions}
              onChange={handleCriteriaChange}
              isClearable={true}
            />
            <FormSelect
              label="criteriaValue"
              name="criteriaValueId"
              value={selectedCriteriaValue}
              register={register}
              errors={errors}
              options={criteriaValueOptions}
              onChange={handleCriteriaValueChange}
              isClearable={true}
            />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="tool-feature-table-container">
        <DataTable
          rows={filteredData}
          columns={columns}
          pageSize={pageSize}
          setPageSize={setPageSize}
          localeTextsMap={localizedTextsMap}
        />
      </div>

      {/* Empty State */}
      {filteredData.length === 0 && (
        <div className="tool-feature-empty">
          <div className="tool-feature-empty-icon">
            <SearchOffIcon style={{ fontSize: '3rem' }} />
          </div>
          <p className="tool-feature-empty-text">{t("noToolFeaturesFound")}</p>
        </div>
      )}
    </ListLayout>
  );
};

export default observer(List);

// import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
// import { toJS } from 'mobx';
// import { observer } from 'mobx-react-lite';
// import { useTranslation } from 'react-i18next';
// import { useStore } from '../../../stores/store';
// import ListLayout from '../../Generic/ListLayout';
// import DataTable from '../../Generic/DataTable';
// import confirmDeleteDialog from '../../Generic/confirmDeleteDialog';
// import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
// import useDeleteItem from '../../../hooks/useDeleteItem';
// import useFilterData from '../../../hooks/useFilterData';
// import useFetchData from '../../../hooks/useFetchData';
// import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
// import { useSetMenu } from "../../../hooks/useSetMenu";
// import { createGeneralColumn, createActiveColumn, createActionColumn } from '../../Generic/dataGridColumns';
// import AuthContext from "../../../context/AuthProvider";
// const List = () => {
//   const axiosPrivate = useAxiosPrivate();
//   const { auth } = useContext(AuthContext);
//   const { mainStore, toolFeatureStore } = useStore();
//   const { t } = useTranslation();
//   const localizedTextsMap = useLocalizedTextsMap();
//   useSetMenu(mainStore.ToolFeatureMenuType, mainStore.ToolFeatureMenuNo);
//   const [columns, setColumns] = useState([]);
//   const [pageSize, setPageSize] = useState(10);
//   const [searchQuery, setSearchQuery] = useState("");

//   //FetchData and Search


//   useFetchData(axiosPrivate, `/ToolFeatures`, toolFeatureStore);
//   const filteredData = useFilterData(toJS(toolFeatureStore.toolFeature), searchQuery);

//   const handleSearchChange = (event) => {
//     setSearchQuery(event.target.value);
//   };

//   //Delete
//   const deleteToolFeature = useDeleteItem(axiosPrivate, '/ToolFeatures', toolFeatureStore.deleteToolFeature);

//   const remove = useCallback((id) => {
//     confirmDeleteDialog(t, deleteToolFeature, id);
//   }, [deleteToolFeature, t]);

//   const cols = useMemo(() => [
//     createGeneralColumn("toolPassportName", "toolPassport", t),
//     createGeneralColumn("criteriaValueName", "criteriaValue", t),
//     createGeneralColumn("criteriaName", "criteria", t),
//     createGeneralColumn("subcategoryName", "subcategory", t),
//     createGeneralColumn("categoryName", "category", t),
//     createGeneralColumn("panelName", "panel", t),
//     createActiveColumn(t),
//     createActionColumn(t, "/ToolFeature/Edit", remove),
//     // eslint-disable-next-line
//   ].filter(Boolean), [t, remove]);

//   useEffect(() => {
//     setColumns(cols);
//     // eslint-disable-next-line
//   }, [t]);

//   return (
//     <>
//       <ListLayout
//         title={t("toolFeatureListTitle")}
//         searchQuery={searchQuery}
//         onSearchChange={handleSearchChange}
//         searchPlaceholder={t("searchPlaceholder")}
//         addButtonLink="/ToolFeature/Add"
//         addButtonTitle={t("add")}
//       >
//         <DataTable
//           rows={filteredData}
//           columns={columns}
//           pageSize={pageSize}
//           setPageSize={setPageSize}
//           localeTextsMap={localizedTextsMap}
//         />
//       </ListLayout>
//     </>
//   );
// };

// export default observer(List);